import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { EndpointAddress, EndpointPort, IEndpointSelector } from '../model'
import Progress from 'core/components/progress/Progress'
import SummaryCard from 'k8s/components/common/entity/summary-card'
import InfoCard from 'k8s/components/common/entity/info-card'
import { getFieldsForCard } from 'core/components/InfoPanel'
import LabelsAndAnnotationsSection from 'k8s/components/common/entity/labels-and-annotations/LabelsAndAnnotationsSection'
import { renderAge } from 'k8s/components/common/entity/helpers'
import Card from 'core/elements/card'
import { OwnerReferences } from 'k8s/components/configuration/config-maps/details/ConfigMapsOverview'
import Grid, { GridViewColumn } from 'core/elements/grid/Grid'

const summaryRows: { key: keyof IEndpointSelector; label: string }[] = [
  { key: 'clusterName', label: 'Cluster' },
  { key: 'namespace', label: 'Namespace' },
  { key: 'age', label: 'Age' },
]

const addressesColumns: GridViewColumn<EndpointAddress>[] = [
  { key: 'ip', label: 'IP' },
  { key: 'hostname', label: 'Hostname' },
  { key: 'targetRef.name', label: 'Target' },
]

const portsColumns: GridViewColumn<EndpointPort>[] = [
  { key: 'port', label: 'Port' },
  { key: 'name', label: 'Name' },
  { key: 'protocol', label: 'Protocol' },
]

const EndpointsOverview = ({
  k8sEndpoint,
  loading,
  reload,
}: {
  k8sEndpoint: IEndpointSelector
  loading: boolean
  reload: (refetch: boolean) => void
}) => {
  const classes = useStyles()

  const metadataFields = [
    {
      id: 'age',
      title: 'Created',
      required: true,
      render: renderAge,
    },
    {
      id: 'ownerReferences',
      title: 'Owner References',
      render: (value) => <OwnerReferences refs={value} />,
      condition: ({ ownerReferences }) => ownerReferences?.length > 0,
    },
  ]

  const metadata = useMemo(() => {
    return getFieldsForCard(metadataFields, k8sEndpoint)
  }, [k8sEndpoint])

  return (
    <Progress loading={loading}>
      <div className={classes.container}>
        <div className={classes.column}>
          <SummaryCard<IEndpointSelector>
            title={k8sEndpoint?.name}
            subtitle="Endpoint"
            rows={summaryRows}
            data={k8sEndpoint}
          />
          <InfoCard
            items={metadata}
            title="Properties"
            footer={
              <div className={classes.footer}>
                <LabelsAndAnnotationsSection entity={k8sEndpoint} resourceType="k8sEndpoint" />
              </div>
            }
          />
        </div>
        <div className={classes.column}>
          <Card title="Subsets Addresses" withCustomBody>
            <Grid
              uniqueIdentifier="ip"
              data={k8sEndpoint?.subsets?.[0]?.addresses || []}
              columns={addressesColumns}
              loading={loading}
              compact
              disableToolbar
            />
          </Card>
          <Card title="Subsets Ports" withCustomBody>
            <Grid
              uniqueIdentifier="port"
              data={k8sEndpoint?.subsets?.[0]?.ports || []}
              columns={portsColumns}
              loading={loading}
              compact
              disableToolbar
            />
          </Card>
        </div>
      </div>
    </Progress>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '633px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutosummaryRows: 'max-content',
  },
  footer: {
    margin: theme.spacing(1),
  },
  copyClipboard: {
    '& .copy': {
      color: theme.components.typography.passive,
    },
  },
}))

export default EndpointsOverview
